import { observer } from "mobx-react-lite";
import { useEffect, useMemo } from "react";
import { reaction, toJS } from "mobx";
import { Loader2 } from "lucide-react";
import { useTran } from "@/hooks";
import { useDashboardFiltersStore } from "../../../hooks/useDashboardStore";
import {
  dashboardEChartsStore,
  E_CHARTS_BASE_YEAR,
  E_CHARTS_MAX_YEAR_RANGE,
} from "../../../stores/e-charts.store";
import { dashboardEChartsTotalsStore } from "../../../stores/e-charts-totals.store";
import { dashboardFiltersStore } from "../../../stores/filters.store";
import {
  buildEChartsRequestContext,
  createEChartsFetchReaction,
  fingerprintOf,
  loadEChartsData,
  noneSelectedDimensionOf,
} from "../../../operations/load-e-charts.ops";
import {
  buildTotalsContextKey,
  ensureKpiStripYears,
  loadEChartsTotals,
} from "../../../operations/load-e-charts-totals.ops";
import {
  E_CHARTS_MAX_ENTITY_IDS,
  normalizeEntityId,
  otherDimension,
} from "../../../utils/e-charts-dimensions";
import {
  deriveDisplayedEntities,
  EMPTY_CAUSE,
  resolveEmptyState,
  resolveRenderMode,
} from "../../../utils/e-charts-view";
import { buildPickerOptions } from "../../../utils/hierarchy-picker";
import { buildEntityColorMap } from "./colors";
import EChartsDrillBreadcrumb from "./EChartsDrillBreadcrumb";
import EChartsFilters from "./EChartsFilters";
import EChartsKpiStrip from "./EChartsKpiStrip";
import EChartsLegend from "./EChartsLegend";
import EChartsHero from "./EChartsHero";
import EChartsSupportingGrid from "./EChartsSupportingGrid";
import EChartsWarnings from "./EChartsWarnings";
import EmissionCardLayout from "./EmissionCardLayout";
import EmissionsBreakdownDrawer from "./EmissionsBreakdownDrawer";

const FETCH_DEBOUNCE_MS = 400;
const FALLBACK_END_YEAR = 2025;

const buildYearRange = (start, end) => {
  const years = [];
  for (let year = start; year <= end; year += 1) years.push(year);
  return years;
};

const getDefaultYearRange = (years) => {
  if (years.length === 0) return { start: null, end: null };
  const end = years[years.length - 1];
  const minStartYear = end - (E_CHARTS_MAX_YEAR_RANGE - 1);
  const startCandidates = years.filter((year) => year >= minStartYear);
  return {
    start: startCandidates[0] ?? years[0],
    end,
  };
};

const buildReportingPeriodStartYears = (reportingYears = []) =>
  [...new Set((reportingYears || []).map((item) => Number(item?.startYear)))]
    .filter(Number.isFinite)
    .sort((left, right) => left - right);

const EChartsPanel = observer(() => {
  const { t } = useTran();
  const { available } = useDashboardFiltersStore();
  const store = dashboardEChartsStore;

  useEffect(() => {
    // Owns its own watched expression so the refetch trigger and the request
    // fingerprint stay one definition (see the ops module).
    const disposeChart = createEChartsFetchReaction({
      delay: FETCH_DEBOUNCE_MS,
    });

    const disposeTotals = reaction(
      () => ({
        frameworks: toJS(dashboardFiltersStore.filters.frameworks),
        years: toJS(store.activeYears),
        reportingYears: toJS(dashboardFiltersStore.available.reportingYears),
      }),
      () => loadEChartsTotals(),
      { delay: FETCH_DEBOUNCE_MS, fireImmediately: true },
    );

    // The KPI strip's selected year is independent of the trend range, so a
    // pick outside the range needs its own totals fetch. Tracking rangeYears
    // (set by each range response) and contextKey covers the re-check after
    // a range narrows past the selection or a filter context changes;
    // fireImmediately covers remounts with a persisted out-of-range pick.
    const totalsStore = dashboardEChartsTotalsStore;
    const disposeKpiYears = reaction(
      () => ({
        selectedYear: store.selectedYear,
        hasLoadedOnce: totalsStore.hasLoadedOnce,
        contextKey: totalsStore.contextKey,
        rangeYears: toJS(totalsStore.rangeYears),
      }),
      () => ensureKpiStripYears(),
      { delay: FETCH_DEBOUNCE_MS, fireImmediately: true },
    );

    return () => {
      disposeChart();
      disposeTotals();
      disposeKpiYears();
    };
  }, [store]);

  const buOptions = useMemo(
    () => buildPickerOptions(available.orgTree, "businessUnit"),
    [available.orgTree],
  );
  const countryOptions = useMemo(
    () => buildPickerOptions(available.orgTree, "country"),
    [available.orgTree],
  );
  const availableYears = useMemo(() => {
    const reportingPeriodYears = buildReportingPeriodStartYears(
      available.reportingYears,
    );

    if (reportingPeriodYears.length > 0) return reportingPeriodYears;
    if (available.years?.length) return available.years;
    return buildYearRange(E_CHARTS_BASE_YEAR, FALLBACK_END_YEAR);
  }, [available.reportingYears, available.years]);

  const optionsFor = (dimension) =>
    dimension === "bu" ? buOptions : countryOptions;

  // Ambient view state: the OTHER dimension while drilled, groupBy otherwise.
  // The request-refusal gates below always read these, but what the charts
  // RENDER may instead come from the data's own context snapshot while a
  // refetch is pending (see renderMode).
  const liveDimension = store.displayedDimension;
  const liveOptions = optionsFor(liveDimension);
  const liveSelection = store.displayedSelection;

  const data = store.data;
  const rows = useMemo(() => data?.rows ?? [], [data]);
  const requestedWithin = store.requestedWithin;

  // Stored rows answer exactly one request context; when the ambient context
  // has moved past it (picker, drill, year, framework, or option change), the
  // rows must not be presented as the current filtered result (Codex round 3
  // finding 1). Reading the context here also subscribes this observer to
  // everything the fetch depends on, so the comparison stays live.
  const ambientContext = buildEChartsRequestContext();
  const ambientFingerprint = fingerprintOf(ambientContext);
  const isStale = store.dataFingerprint !== ambientFingerprint;

  // Mirror of the ops preflight (Brief D): which selection, if any, is an
  // explicit none. Diagnosed from the ambient context so the panel and the
  // refusal can never disagree.
  const noneSelectedDimension = noneSelectedDimensionOf(ambientContext);

  // Mirror of the request-refusal rule in the ops module: name the dimension
  // whose list would breach the API bound so the message can say which
  // selection to narrow. Always the LIVE selection: the refusal is about the
  // request the ambient context would send. A none selection counts as ZERO
  // entities (Brief D): it must never read as an over-limit All enumeration.
  const displayedRequestCount =
    liveSelection.mode === "subset"
      ? liveSelection.ids.length
      : liveSelection.mode === "none"
        ? 0
        : liveOptions.length;
  const liveWithinSelection = store.selectionFor(otherDimension(liveDimension));
  const withinRequestCount = store.drillContext
    ? 1
    : liveWithinSelection.mode === "subset"
      ? liveWithinSelection.ids.length
      : 0;
  const overLimitDimension =
    displayedRequestCount > E_CHARTS_MAX_ENTITY_IDS
      ? liveDimension
      : withinRequestCount > E_CHARTS_MAX_ENTITY_IDS
        ? otherDimension(liveDimension)
        : null;

  // The org tree with nodes is the signal that the vocabulary question is
  // settled: an empty option list is then a fact about the tenant (no
  // tagged countries), not about loading.
  const vocabularySettled = (available.orgTree?.length ?? 0) > 0;

  // While a refetch is pending the previous view stays mounted AS the
  // previous view: dimension, selection, drill name, and years all come from
  // the snapshot stored beside the rows, so stale data is presented as the
  // outgoing view in transition, never as the answer to the new question
  // (which preserves the Codex round 3 finding 1 guarantee). Data and
  // headings then swap together in one render when the fingerprint catches
  // up.
  const snapshot = store.dataContext;
  const refreshing =
    resolveRenderMode({
      liveYearCount: store.activeYears.length,
      noneSelectedDimension,
      overLimitDimension,
      loadError: store.loadError,
      vocabularySettled,
      liveOptionCount: liveOptions.length,
      isStale,
      hasLoadedOnce: store.hasLoadedOnce,
      hasSnapshot: Boolean(snapshot),
    }) === "refreshing";

  const displayedDimension = refreshing
    ? snapshot.displayedDimension
    : liveDimension;
  const viewDrillContext = refreshing
    ? snapshot.drillContext
    : store.drillContext;
  const displayedSelection = refreshing ? snapshot.selection : liveSelection;
  const withinDimension = otherDimension(displayedDimension);
  // Options from the snapshot while refreshing: an "all" selection
  // materializes membership, names, and colors from the option list, so an
  // org-tree reload mid-refresh must not rewrite the outgoing view ahead of
  // the swap (this build's Codex finding 1).
  const entityOptions = refreshing
    ? snapshot.options[displayedDimension]
    : optionsFor(displayedDimension);
  const withinOptions = refreshing
    ? snapshot.options[withinDimension]
    : optionsFor(withinDimension);

  const effectiveEntities = useMemo(
    () =>
      deriveDisplayedEntities({
        dimension: displayedDimension,
        selection: displayedSelection,
        optionIds: entityOptions.map((option) => String(option.id)),
        rows,
        withinSent: Boolean(requestedWithin),
      }),
    [
      displayedDimension,
      displayedSelection,
      entityOptions,
      requestedWithin,
      rows,
    ],
  );
  const entityNameById = useMemo(() => {
    const m = new Map();
    entityOptions.forEach((o) => m.set(String(o.id), o.name));
    return m;
  }, [entityOptions]);
  // Names for the within dimension, keyed by the normalized id the API echoes
  // back in UNKNOWN_WITHIN_ENTITY, so an ignored selection is named rather
  // than shown as a raw key.
  const withinNameById = useMemo(() => {
    const m = new Map();
    withinOptions.forEach((o) =>
      m.set(normalizeEntityId(withinDimension, o.id), o.name),
    );
    return m;
  }, [withinDimension, withinOptions]);
  // Colors come from the full option list, not the selection, so each entity
  // keeps one fixed color no matter which subset is picked (R09 items 3/4).
  const entityColorById = useMemo(
    () => buildEntityColorMap(entityOptions.map((o) => o.id)),
    [entityOptions],
  );

  const entities = effectiveEntities;
  const years = refreshing ? snapshot.years : store.activeYears;

  const emptyState = resolveEmptyState({
    years,
    entities,
    optionCount: entityOptions.length,
    rows,
    warnings: store.warnings,
    requestedWithin,
    hasLoadedOnce: store.hasLoadedOnce,
    noneSelectedDimension,
    overLimitDimension,
    loadError: store.loadError,
    // While refreshing, the rows are being rendered as the view they answer,
    // so relative to THAT view they are not stale; the veil carries the
    // in-transition story instead of the NOT_LOADED gate.
    isStale: refreshing ? false : isStale,
    vocabularySettled,
    // A finite zero IS data; only null and undefined count as nothing.
    hasAnyValue: () =>
      entities.some((id) =>
        years.some((year) => store.totalFor(id, year) != null),
      ),
  });

  // Standing no-jump rule (2026-08-09): nothing mounts or unmounts on entity
  // ticking, unticking, or drilling. The tenant-wide strip and totals cards
  // used to step aside on any narrowed or drilled view (brief section 5 open
  // item 8, option a) and the drill scroll-anchoring compensated for that
  // collapse; both are gone. The strip and cards stay mounted on every view
  // and label themselves company-wide, so the page keeps its height and the
  // scroll position needs no correction.

  const latestAvailableYear =
    availableYears.length > 0
      ? availableYears[availableYears.length - 1]
      : null;

  useEffect(() => {
    store.setAvailableYears(availableYears);
    if (availableYears.length === 0) return;
    store.applyDefaultYearRange(getDefaultYearRange(availableYears));
  }, [availableYears, store]);

  // Default the KPI strip to the newest year that actually has totals data
  // (a boundary can extend years past the last submission, e.g. to 2036).
  // Hold off until the first totals response; fall back to the newest
  // boundary year when no year has data at all. Reading the totals summary
  // here (render scope) keeps the observer tracking it. The store keeps an
  // applied auto selection sticky within a filter context, so this candidate
  // only lands at first load, on context rotation, or when the current year
  // leaves the boundary; the totals summary is range-scoped, so recomputing
  // it on a range change must not move an existing selection. The context
  // key is an effect dependency because a rotation can produce the same
  // candidate year (e.g. baseline) and still needs to re-apply it.
  const totalsStore = dashboardEChartsTotalsStore;
  const totalsContextKey = totalsStore.contextKey;

  // Pinned Y scales survive every entity interaction by design; only a
  // genuine filter-context change (frameworks / reporting periods) starts a
  // fresh scale. The fingerprint is computed from the LIVE filters here, not
  // from the totals response's stored key, and travels into every chart's
  // axis key: a filter change therefore reads fresh marks on its very next
  // render, with no reset ordering to race and nothing missed when the new
  // context's totals request is slow or fails (Codex round, finding 1).
  const axisContextKey = buildTotalsContextKey();
  const defaultSelectedYear = totalsStore.hasLoadedOnce
    ? (totalsStore.latestYearWithData(availableYears) ?? latestAvailableYear)
    : null;

  // availableYears is a dependency because a boundary edit can remove the
  // sticky year without changing the candidate or the context key; the
  // store-side membership guard only helps if this effect actually runs.
  useEffect(() => {
    if (defaultSelectedYear == null) return;
    store.applyDefaultSelectedYear(defaultSelectedYear, totalsContextKey);
  }, [defaultSelectedYear, totalsContextKey, availableYears, store]);

  return (
    <div className="space-y-3">
      <EChartsKpiStrip />
      <EmissionsBreakdownDrawer />
      <EChartsWarnings
        orgTree={available.orgTree}
        displayedDimension={displayedDimension}
        entityNameById={entityNameById}
        effectiveEntities={effectiveEntities}
        withinNameById={withinNameById}
        suppressUnknownWithin={
          emptyState?.cause === EMPTY_CAUSE.INVALID_SELECTION
        }
        dimmed={refreshing}
      />
      <EmissionCardLayout
        availableYears={availableYears}
        reportingYears={available.reportingYears}
      />
      <EChartsFilters buOptions={buOptions} countryOptions={countryOptions} />
      {/* The stale-view veil. While a refetch is pending the block below
          keeps rendering the PREVIOUS view from the data's own context
          snapshot: dimmed, inert (pointer and keyboard), flagged busy, with
          one status pill. Nothing unmounts, so the page keeps its height and
          scroll position instead of collapsing by the full chart stack for
          the duration of every refetch (the R09 item 5 redraw defect). The
          filters above stay live so the user can keep editing the request. */}
      <div className="relative">
        {refreshing && (
          <div
            role="status"
            className="absolute left-1/2 top-2 z-10 flex -translate-x-1/2 items-center gap-1.5 rounded-full border border-border/70 bg-card px-2.5 py-1 text-[11px] font-medium text-muted-foreground shadow-sm"
          >
            <Loader2 className="h-3 w-3 animate-spin" aria-hidden="true" />
            {t("Updating...")}
          </div>
        )}
        <div
          inert={refreshing}
          aria-busy={refreshing}
          className={`space-y-3 transition-opacity ${
            refreshing ? "opacity-60" : ""
          }`}
        >
          {/* Reserved slot: the trail only has content during a drill, but
              its line of height is always there so entering or leaving a
              drill never pushes the hero (standing no-jump rule). */}
          <div className="min-h-6">
            <EChartsDrillBreadcrumb drillContext={viewDrillContext} />
          </div>
          <EChartsHero
            entities={entities}
            years={years}
            entityNameById={entityNameById}
            entityColorById={entityColorById}
            displayedDimension={displayedDimension}
            axisContextKey={axisContextKey}
            drillContext={viewDrillContext}
            refreshing={refreshing}
            emptyState={emptyState}
            onEmptyStateAction={(kind) => {
              if (kind === "retry") loadEChartsData();
              if (kind === "exit-drill") store.exitDrill();
            }}
          />
          {/* The legend and the three scope charts stay mounted through
              refreshes AND empty states (standing no-jump rule): while the
              hero is explaining an empty view they draw empty plots on their
              pinned axes instead of unmounting, which used to collapse the
              page by three cards on every untick-All. The legend lives in a
              FIXED-height slot (two comfortable rows, scrolling beyond) so
              wrap-count changes from ticking entities never move the grid
              below (Codex round, finding 3). */}
          <div className="h-12 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center">
              <EChartsLegend
                entities={entities}
                years={years}
                entityNameById={entityNameById}
                entityColorById={entityColorById}
              />
            </div>
          </div>
          <EChartsSupportingGrid
            entities={entities}
            years={years}
            entityNameById={entityNameById}
            entityColorById={entityColorById}
            displayedDimension={displayedDimension}
            axisContextKey={axisContextKey}
            drillContext={viewDrillContext}
            refreshing={refreshing}
          />
        </div>
      </div>
    </div>
  );
});

export default EChartsPanel;
